import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from '../../components/input/input';
import { DataPicker } from '../../components/data-picker/data-picker';
import { AdminService } from '../../services/admin-service';
import { Loading } from '../../components/loading/loading';
import { Select } from '../../components/select/select';

interface CloudWatchLog {
  eventId: string;
  timestamp: number;
  ingestionTime: number;
  logStreamName: string;
  message: string;
}

@Component({
  selector: 'app-logs',
  imports: [NgClass, FormsModule, ReactiveFormsModule, InputComponent, DataPicker, Loading, Select],
  templateUrl: './logs.html',
})
export class Logs implements OnInit {
  rangeData: [Date, Date] = [new Date(), new Date()];
  loading = false;
  logs: CloudWatchLog[] | [] = [];
  filteredLogs: CloudWatchLog[] = [];
  filterText = '';
  sortDirection: 'asc' | 'desc' = 'desc';
  type = new FormControl<'Error' | 'Debug' | 'Warning'>('Error');
  typeOptions = [
    { label: 'Error', value: 'Error' },
    { label: 'Debug', value: 'Debug' },
    { label: 'Warning', value: 'Warning' },
  ];

  constructor(private adminService: AdminService) {
    this.type.valueChanges.subscribe((value) => {
      this.getLogs();
    });
  }

  ngOnInit() {
    this.rangeData[0].setHours(0);
    this.rangeData[1].setHours(23);
    this.rangeData[0].setDate(this.rangeData[0].getDate() - 1);

    this.getLogs();
  }

  onRangeChanged(value: [Date, Date]) {
    this.rangeData = value;
    this.getLogs();
  }

  applyFilter() {
    const search = this.filterText.toLowerCase();
    this.filteredLogs = this.logs.filter(
      (log) =>
        log.message.toLowerCase().includes(search) ||
        log.logStreamName.toLowerCase().includes(search)
    );
  }

  toggleSort() {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredLogs.sort((a, b) => (a.timestamp - b.timestamp) * direction);
  }

  getMessageClass(message: string): string {
    if (message.toLowerCase().includes('[error]')) return 'text-red-600 font-semibold';
    if (message.toLowerCase().includes('[warning]')) return 'text-yellow-600 font-semibold';
    return 'text-gray-700';
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
  }

  getLogs() {
    this.loading = true;
    this.adminService
      .getLogs(this.rangeData[0], this.rangeData[1], this.type.value!)
      .subscribe((resp) => {
        this.logs = [...resp];
        this.filteredLogs = [...resp];
        this.loading = false;
      });
  }

  extractAndFormatJson(log: string): string {
    let start = -1;
    let end = -1;
    let balance = 0;

    for (let i = log.length - 1; i >= 0; i--) {
      if (log[i] === '}') {
        end = i;
        break;
      }
    }

    if (end === -1) return log;

    for (let i = end; i >= 0; i--) {
      if (log[i] === '}') balance++;
      if (log[i] === '{') balance--;

      if (balance === 0) {
        start = i;
        break;
      }
    }

    if (start === -1) return log;

    const jsonStr = log.substring(start, end + 1);

    try {
      const obj = JSON.parse(jsonStr);
      const formatted = JSON.stringify(obj, null, 2);

      return log.slice(0, start) + formatted;
    } catch {
      return log;
    }
  }
}
